/**
 * Homeowner issue submission dialog.
 * Category picker, description field, optional photo upload (1-3),
 * submit to submit-homeowner-issue edge function.
 */

import { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, CheckCircle2, Camera, X, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { invokeAuthedFunction } from '@/lib/invokeAuthedFunction';
import { useApp } from '@/contexts/AppContext';

export const HOMEOWNER_CATEGORIES = [
  { value: 'power_went_out', label: 'Power went out' },
  { value: 'generator_sounds_wrong', label: 'Generator sounds wrong' },
  { value: 'inverter_error', label: 'Inverter error' },
  { value: 'storm_damage', label: 'Storm damage' },
  { value: 'roof_leak', label: 'Roof leak' },
  { value: 'low_production', label: 'Low production' },
  { value: 'app_says_offline', label: 'App says offline' },
  { value: 'battery_not_charging', label: 'Battery not charging' },
  { value: 'other', label: 'Other' },
] as const;

const MAX_PHOTOS = 3;

interface SelectedPhoto {
  file: File;
  preview: string;
}

interface ReportIssueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  onSuccess?: () => void;
}

export function ReportIssueDialog({
  open,
  onOpenChange,
  propertyId,
  onSuccess,
}: ReportIssueDialogProps) {
  const { organization } = useApp();
  const orgId = organization?.id;
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState<SelectedPhoto[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canSubmit = category && description.trim().length >= 10 && !submitting;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const remaining = MAX_PHOTOS - photos.length;
    const newFiles = Array.from(files).slice(0, remaining);
    const newPhotos: SelectedPhoto[] = newFiles
      .filter((f) => f.type.startsWith('image/'))
      .map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));
    setPhotos((prev) => [...prev, ...newPhotos].slice(0, MAX_PHOTOS));
    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  const removePhoto = (index: number) => {
    setPhotos((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const uploadPhotos = async (): Promise<string[]> => {
    if (!orgId || photos.length === 0) return [];
    const paths: string[] = [];
    for (let i = 0; i < photos.length; i++) {
      setUploadProgress(`Uploading photo ${i + 1} of ${photos.length}...`);
      const photo = photos[i];
      const ext = photo.file.name.split('.').pop() ?? 'jpg';
      const filename = `${crypto.randomUUID()}.${ext}`;
      const path = `${orgId}/${propertyId}/homeowner-submissions/${filename}`;
      const { error } = await supabase.storage
        .from('service-photos')
        .upload(path, photo.file, { contentType: photo.file.type });
      if (error) throw new Error(`Photo upload failed: ${error.message}`);
      paths.push(path);
    }
    setUploadProgress('');
    return paths;
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Upload photos first
      let photoPaths: string[] = [];
      try {
        photoPaths = await uploadPhotos();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Photo upload failed';
        toast.error(msg);
        setSubmitting(false);
        return;
      }

      const result = await invokeAuthedFunction<{ issue_id?: string; error?: string }>(
        supabase,
        'submit-homeowner-issue',
        {
          property_id: propertyId,
          category,
          description: description.trim(),
          ...(photoPaths.length > 0 ? { photo_paths: photoPaths } : {}),
        },
      );
      if (result.error) {
        toast.error(result.error);
      } else {
        setSubmitted(true);
        onSuccess?.();
      }
    } catch {
      toast.error('Failed to submit issue');
    } finally {
      setSubmitting(false);
      setUploadProgress('');
    }
  };

  const handleClose = (newOpen: boolean) => {
    if (!newOpen) {
      // Cleanup previews
      photos.forEach((p) => URL.revokeObjectURL(p.preview));
      setCategory('');
      setDescription('');
      setPhotos([]);
      setSubmitted(false);
      setUploadProgress('');
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Report an Issue</DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="flex flex-col items-center py-6 gap-3">
            <CheckCircle2 className="w-10 h-10 text-wt-green-primary" />
            <p className="text-sm font-medium">Issue submitted</p>
            <p className="text-xs text-muted-foreground text-center">
              {/* WT-HARNESS-391: the provider notification is fire-and-forget and may be skipped (email
                  disabled / no recipient), so we don't claim delivery. The issue IS persisted and visible
                  to the provider — that is what's actually true. */}
              Your issue has been logged. Your service provider can see it in their dashboard and will review it.
            </p>
            <Button variant="outline" size="sm" onClick={() => handleClose(false)}>
              Close
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>What&apos;s happening?</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {HOMEOWNER_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what you're experiencing..."
                  rows={4}
                  className="resize-none"
                />
                {description.length > 0 && description.trim().length < 10 && (
                  <p className="text-xs text-wt-red-primary">
                    Please provide at least 10 characters
                  </p>
                )}
              </div>

              {/* Photo upload */}
              <div className="space-y-2">
                <Label>Photos (optional, up to {MAX_PHOTOS})</Label>
                {photos.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {photos.map((photo, idx) => (
                      <div
                        key={idx}
                        className="relative w-20 h-20 rounded-md overflow-hidden border border-border group"
                      >
                        <img
                          src={photo.preview}
                          alt={`Photo ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(idx)}
                          className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label={`Remove photo ${idx + 1}`}
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {photos.length < MAX_PHOTOS && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="photo-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full"
                    >
                      <Camera className="w-4 h-4 mr-1.5" />
                      {photos.length === 0 ? 'Add photo' : 'Add another photo'}
                    </Button>
                  </>
                )}
                {uploadProgress && (
                  <p className="text-xs text-muted-foreground">{uploadProgress}</p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={!canSubmit}>
                {submitting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                Submit
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
